import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { publicApi } from './api';

const DEFAULT_RATES = { EUR: 1.0, USD: 1.08, GBP: 0.85, CHF: 0.96 };

export const CURRENCY_SYMBOLS = { EUR: '€', USD: '$', GBP: '£', CHF: 'CHF' };
export const CURRENCY_LABELS = { EUR: 'Euro', USD: 'US Dollar', GBP: 'British Pound', CHF: 'Swiss Franc' };

const CurrencyCtx = createContext(null);

export function CurrencyProvider({ children }) {
  const [currency, setCurrency] = useState(() => localStorage.getItem('yup_currency') || 'EUR');
  const [rates, setRates] = useState(() => {
    try { return JSON.parse(localStorage.getItem('yup_currency_rates')) || DEFAULT_RATES; }
    catch { return DEFAULT_RATES; }
  });

  useEffect(() => {
    publicApi.currencyRates().then(({ rates }) => {
      if (rates && rates.EUR) {
        setRates(rates);
        localStorage.setItem('yup_currency_rates', JSON.stringify(rates));
      }
    }).catch(() => {});
  }, []);

  useEffect(() => { localStorage.setItem('yup_currency', currency); }, [currency]);

  /** Convert an amount expressed in EUR to the selected currency and return a formatted string. */
  const format = useCallback((amountEur, opts = {}) => {
    if (typeof amountEur !== 'number' || isNaN(amountEur)) return '';
    const rate = rates[currency] || 1;
    const converted = amountEur * rate;
    const symbol = CURRENCY_SYMBOLS[currency] || '';
    const decimals = opts.decimals ?? (converted >= 100 ? 0 : (converted >= 10 ? 0 : 1));
    return `${symbol}${converted.toFixed(decimals)}`;
  }, [rates, currency]);

  return (
    <CurrencyCtx.Provider value={{ currency, setCurrency, rates, format }}>
      {children}
    </CurrencyCtx.Provider>
  );
}

export function useCurrency() {
  const ctx = useContext(CurrencyCtx);
  if (!ctx) throw new Error('useCurrency must be used inside CurrencyProvider');
  return ctx;
}
